import { Image, Inspector, Flex } from '@sequencehq/core-components'
import { FC, ReactNode } from 'react'
import { quickBooksIntegrationConfig } from '../quickbooks.integration.config'
import { LinkCustomerToServiceWidget } from '../../common/LinkEntities/LinkCustomerToService'
import { useIntegrationActive } from 'Integrations/utils/useIntegrationActive'
import { SmallIcon } from 'Integrations/integrationsConfig/common/SmallIcon'
import { LinkedInvoiceWidget } from 'Integrations/integrationsConfig/common/LinkEntities/LinkedInvoiceWidget/LinkedInvoiceWidget'
import { useIntegrationAuthError } from 'Integrations/utils/useIntegrationAuthError'
import { NotAuthenticatedItemContent } from 'Integrations/integrationsConfig/common/NotAuthenticatedItemContent'
import { LinkProductWidget } from 'Integrations/integrationsConfig/common/LinkEntities'
import { LinkOrCreateCustomerModal } from 'Integrations/integrationsConfig/common/LinkEntities/LinkCustomerToService/LinkOrCreateCustomerModal'
import { LinkQuickbooksItemModal } from 'Integrations/integrationsConfig/quickBooks/InspectorItem/LinkQuickbooksItemModal'
import { LinkedCreditNoteWidget } from 'Integrations/integrationsConfig/common/LinkEntities/LinkedCreditNoteWidget/LinkedCreditNoteWidget'
import { AccountLinkWidget } from 'Integrations/integrationsConfig/common/LinkEntities/AccountLinkWidget/AccountLinkWidget'
import { useQuickBooksCustomerFields } from './useQuickBooksCustomerFields'

export interface InspectorItemProps {
  customerId: string
}

export const LinkQuickbooksProductWidget = (props: { productId: string }) => (
  <LinkProductWidget
    productId={props.productId}
    service="QuickBooks_Online"
    sequenceEntityLabel="Product"
    externalEntityLabel="Item"
    linkModal={LinkQuickbooksItemModal}
  />
)

export const LinkQuickbooksAccountWidget = (props: { productId: string }) => (
  <AccountLinkWidget
    productId={props.productId}
    sequenceEntityLabel="Product"
    externalEntityLabel="Account"
    service="QuickBooks_Online"
  />
)

export const LinkQuickbooksCustomerWidget: FC<{
  customerId: string
  canUnlink?: boolean
}> = props => {
  const {
    serviceSpecificFields,
    serviceSpecificFormFields,
    serviceSpecificQueries
  } = useQuickBooksCustomerFields({
    skip: false
  })

  return (
    <LinkCustomerToServiceWidget
      customerId={props.customerId}
      sequenceEntityLabel="customer"
      externalEntityLabel="customer"
      service="QuickBooks_Online"
      canUnlink={props.canUnlink}
      linkModal={LinkOrCreateCustomerModal}
      linkModalProps={{
        serviceSpecific: {
          fields: serviceSpecificFields,
          formFields: serviceSpecificFormFields,
          isValid: serviceSpecificQueries.isValid
        }
      }}
    />
  )
}

export const LinkQuickbooksInvoiceWidget: FC<{
  invoiceId: string
}> = props => (
  <LinkedInvoiceWidget
    invoiceId={props.invoiceId}
    service="QuickBooks_Online"
  />
)

export const LinkQuickbooksCreditNoteWidget: FC<{
  creditNoteId: string
}> = props => (
  <LinkedCreditNoteWidget
    creditNoteId={props.creditNoteId}
    service="QuickBooks_Online"
  />
)

export const QuickBooksInspectorItem: FC<{
  children: ReactNode
}> = props => {
  const integrationActive = useIntegrationActive('QuickBooks_Online')
  const integrationAuthError = useIntegrationAuthError('QuickBooks_Online')

  if (!integrationActive && !integrationAuthError) {
    return null
  }

  return (
    <Inspector.IconItem
      data-testid="quickbooks.inspector.item"
      content={
        integrationAuthError ? (
          <NotAuthenticatedItemContent
            service="QuickBooks_Online"
            data-testid="quickbooks.inspector.content.unauth"
            serviceDescription={quickBooksIntegrationConfig.description}
            serviceName={quickBooksIntegrationConfig.title}
            logo={
              <Image
                src={quickBooksIntegrationConfig.smallLogoUrl}
                height="24px"
                width="24px"
              />
            }
          />
        ) : (
          <Inspector.Content
            data-testid="quickbooks.inspector.content"
            title="QuickBooks"
            icon={<SmallIcon url={quickBooksIntegrationConfig.smallLogoUrl} />}
          >
            <Flex flexDirection="column" gap="16px">
              {props.children}
            </Flex>
          </Inspector.Content>
        )
      }
      tooltip="QuickBooks"
      icon={<SmallIcon url={quickBooksIntegrationConfig.smallLogoUrl} />}
    />
  )
}
